/**
 * Order Import API Connector
 *
 * Client-side integration layer for order import functionality.
 * Handles API communication for template fetching and file processing.
 */

import * as Sentry from '@sentry/nextjs';

import type {
  OrderImportCommitRequest,
  OrderImportCommitResponse,
  OrderImportProcessResponse,
  OrderImportRevalidateRequest,
  OrderImportTemplateResponse,
} from './types';

type OrderImportProcessPayload = {
  fileName: string;
  mimeType: string;
  contentBase64: string;
  locale?: string;
};

async function fetchApi<T>(url: string, method: 'GET' | 'POST', body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    const message = errorBody?.message ?? `Request failed (${response.status})`;

    throw new Error(message);
  }

  return (await response.json()) as T;
}

/**
 * Fetches order import template download URL from API.
 * Used to provide users with a sample file for formatting their imports.
 *
 * @param tag - Template identifier (e.g., 'orderImportTemplate')
 * @param locale - Optional. ISO locale code (e.g., 'nl', 'fr') for locale-specific templates
 * @returns Promise resolving to template response with URL (null if asset not found). Returns null if fetch fails.
 *
 * @example
 * const response = await fetchOrderImportTemplateUrl('orderImportTemplate', 'nl');
 * if (response?.url) {
 *   window.open(response.url, '_blank');
 * }
 */
const fetchOrderImportTemplateUrl = async (tag: string, locale?: string) => {
  try {
    const params = new URLSearchParams({ tag });

    if (locale) {
      params.set('locale', locale);
    }

    return await fetchApi<OrderImportTemplateResponse>(`/api/order-import/template?${params.toString()}`, 'GET');
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      tags: {
        type: 'Fetch order import template url',
      },
    });

    return null;
  }
};

/**
 * Sends order import payload to the API for processing and validation.
 *
 * @param payload - File metadata and base64 content to process
 * @returns Promise resolving to processed rows with validation results
 * @throws Error if request fails
 *
 * @example
 * const result = await processOrderImportFile({
 *   fileName: csvFile.name,
 *   mimeType: csvFile.type,
 *   contentBase64,
 *   locale: 'nl',
 * });
 * console.log(`Valid rows: ${result.validRows} / ${result.totalRows}`);
 * result.rows.forEach(row => {
 *   if (row.errors.length > 0) {
 *     console.error(`Line ${row.lineNumber}: ${row.errors.join(', ')}`);
 *   }
 * });
 */
const processOrderImportFile = async (payload: OrderImportProcessPayload) => {
  try {
    return await fetchApi<OrderImportProcessResponse>('/api/order-import/process', 'POST', payload);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      tags: {
        type: 'Process order import file',
      },
    });

    throw error;
  }
};

const revalidateOrderImportRows = async (payload: OrderImportRevalidateRequest) => {
  try {
    return await fetchApi<OrderImportProcessResponse>('/api/order-import/revalidate', 'POST', payload);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      tags: {
        type: 'Revalidate order import rows',
      },
    });

    throw error;
  }
};

const commitOrderImportRows = async (payload: OrderImportCommitRequest) => {
  try {
    return await fetchApi<OrderImportCommitResponse>('/api/order-import/commit', 'POST', payload);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      tags: {
        type: 'Commit order import rows',
      },
    });

    throw error;
  }
};

export { fetchOrderImportTemplateUrl, processOrderImportFile, revalidateOrderImportRows, commitOrderImportRows };
